/**
 * Summary
 * This Modal component is responsible for displaying helpful information
 * about CHRONOS and how to get more information.
 */

import React, { useState, useEffect } from "react";
import { useModal, useMaps, useTime } from "../_contexts";
import { ChronosDate } from "../_helpers";
import { HelpForm } from "./forms";
import {
  IconTextInput,
  Modal,
  LocationMarkerPicker,
  TagInput,
  ArticlePicker,
  CalendarDatePicker,
  SubGroupPicker,
  RelatedArticlePicker,
  FileDropzone,
  ZoomControlsField,
  TutorialContainer,
} from "../_components";

export function HelpModal(props) {
  const { firstTime } = props;
  const [devMode, setDevMode] = useState(false);
  const [subGroups, setSubGroups] = useState([]);

  const [articles, setArticles] = useState([]);

  const [dateValue, setDateValue] = useState("746464646464");
  // const [dateValue, setDateValue] = useState(null);
  const { setZoom, goTo, coords } = useMaps();

  const [image, setImage] = useState(null);

  const { calendar } = useTime();

  const handleCalChange = (ev) => {
    setDateValue(ev);
    console.log("Calendar Date Changed: ", ev);
  };

  function handleZoom() {
    setZoom(3);
  }

  function increaseZoom() {
    console.log("Pre-zoom", coords.z);
    setZoom(coords.z + 1);
  }

  function decreaseZoom() {
    setZoom(coords.z - 1);
  }

  function handlePosition() {
    goTo({ x: 24, y: 80 });
  }
  const { closeModal, openModal } = useModal();

  const handleLocationChange = (val) => {
    console.log("Location Changed to", val);
  };
  const handleTagsChange = (tags) => {
    console.log("Tags List", tags);
  };

  const handleToggleDevMode = (ev) => {
    // console.log(ev);
    if (ev.key === "`" && ev.ctrlKey) {
      setDevMode(!devMode);
    }
  };

  const handleTestsClick = (ev) => {
    openModal("chronosTest");
  };

  useEffect(() => {
    window.addEventListener("keydown", handleToggleDevMode);
    return () => window.removeEventListener("keydown", handleToggleDevMode);
  });
  const handleTestClick = (ev) => {
    ev.preventDefault();

    const testSuite = [
      { value: "0", name: "Test Zero" },
      { value: "10", name: "Test Small" },
      { value: "154613562", name: "Test Normal" },
      { value: "-154613562", name: "Test Normal" },
      { value: "15461356264654", name: "Test Normal" },
      { value: "-15461356264654", name: "Test Normal" },
      { value: "842513", name: "Test Normal" },
      { value: "-842513", name: "Test Negative" },
      { value: "-13138513", name: "Test Negative Normal" },
      { value: "-2692", name: "Test Negative Small" },
      { value: "746464646464", name: "Test CUSTOM" },
    ];

    for (let t = 0; t < testSuite.length; t++) {
      const test = testSuite[t];
      const name =
        test.name.length > 0 ? `[TEST ${t}]: (${test.name})` : `[TEST ${t}]: `;
      console.group(name, test.value);
      const date = new ChronosDate(test.value, { calendar });
      const dateObj = date.toFullObject();
      console.log("Absolute Object: ", dateObj);
      // console.log('Relative Object (Native): ', date.toRelativeDate());
      console.log("Relative Object (Argument): ", date.toRelativeDate(dateObj));
      // console.log('Conversion To String (Native): ', date.setDateFromObject());
      console.log(
        "Conversion to String (Argument): ",
        date.setDateFromObject(dateObj)
      );
      console.groupEnd();
    }
  };

  const handleImageChange = (ev) => {
    console.log("IMAGE EVENT", ev);
    if (ev.target) {
      // setImage(ev.target.value);
    } else {
      setImage(ev);
    }
  };

  const handleZoomFieldsChange = ({ min, max }) => {
    console.log("Min: ", min);
    console.log("Max: ", max);
  };

  const handleUploadSuccess = (e) => {
    console.log("handleUploadSuccess: ", e);
  };

  const handleUploadFailure = (e) => {
    console.log("handleUploadFailure: ", e);
  };

  return (
    <Modal
      id="helpModal"
      // backdrop
      title="Help"
      icon="fas fa-question-circle"
      size="md"
      open={true}
      onClose={() => closeModal()}
      noPadding
    >
      <div className="chronos-help-modal">
        {!devMode && <HelpForm firstTime={firstTime} />}
        {devMode && (
          <React.Fragment>
            <h3 className="chronos-subtitle">Developer Mode</h3>
            <p>
              The following options are just tests for various components, as
              well as buttons to quickly access certain forms in Chronos. Feel
              free to play with them, but DO NOT "Save" (Create, Read, Update,
              or Delete) any data with them! This has the potential to corrupt
              your Chronicle and it cannot be recovered! If you accidently see
              this, and want to return to safety, please press{" "}
              <kbd>CTRL + `</kbd>.
            </p>

            <TutorialContainer
              type="video"
              url="https://wa-cdn.nyc3.cdn.digitaloceanspaces.com/chronos/243f1e5e-6048-4ba3-8501-d1d4060d7f15.webm"
            />

            <ZoomControlsField
              onChange={handleZoomFieldsChange}
              min={-5}
              max={5}
              defaultValue={0}
            />

            <FileDropzone
              value={image}
              onChange={handleImageChange}
              fileTypes="image/jpeg,image/png,image/jpg,image/svg,image/vmp"
              htmlImage
              className="chronos-file-dropzone"
              autoUpload
              onUploadSuccess={handleUploadSuccess}
              onUploadFail={handleUploadFailure}
            />
            <ArticlePicker />
            <button onClick={() => openModal("intro")}>Open Intro Modal</button>
            <button onClick={() => openModal("newTimeline")}>
              Open New Timeline Modal
            </button>
            <button onClick={handleZoom}>Set Map Zoom</button>
            <button onClick={handlePosition}>Set Map position</button>
            <button onClick={() => openModal("mapManager")}>Map Manager</button>
            <button onClick={increaseZoom}>Zoom +</button>
            <button onClick={decreaseZoom}>Zoom -</button>
            <IconTextInput value="" />
            <LocationMarkerPicker
              value="60927eac9977ac00aca7b84b"
              onChange={handleLocationChange}
            />
            <TagInput
              value={["one", "two"]}
              // value={tagObjects}
              onChange={handleTagsChange}
              tagOnComma
              transform="capitalize"
              disallowHashes
            />
            <button onClick={() => openModal("timelineManager")}>
              Timeline Manager
            </button>
            <button
              onClick={() => openModal("imageManager", { prevModal: "help" })}
            >
              Image Manager
            </button>
            <button onClick={() => openModal("onboardingEnd")}>
              OnboardingEnd
            </button>
            <br />
            <button onClick={handleTestClick}>Test Date</button>
            <h4>Calendar Default</h4>
            <CalendarDatePicker
              value={dateValue}
              calendar={calendar}
              onChange={handleCalChange}
              clearable //Can the user clear the date?
              placeholder="Select Date"
              defaultStartDate="746464646464"
            />
            <p>{dateValue && dateValue !== null && dateValue.toString()}</p>
            <button onClick={handleTestsClick}>Tests</button>
            <SubGroupPicker value={subGroups} />
            <br />
            {/* {articles.map((article) => (
              <span key={article.waID}>{article.link}</span>
            ))} */}
            <RelatedArticlePicker
              articleType="character"
              value={articles}
              onChange={setArticles}
            />
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
}
